import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Shield,
  Check,
  X,
  Calendar,
  Edit,
  Trash,
  AlertTriangle,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { motion, AnimatePresence } from 'framer-motion'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useGetRolesQuery,
  Role,
  useDeleteRoleMutation,
} from '@/redux/service/userManagement'
import { Button } from '../ui/button'
import { useDebounce } from '@/lib/useDebounce'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

import UpsertRole from './UpsertRole'
import { useState, useEffect } from 'react'

interface RolesProps {
  searchTerm: string
  setSearchTerm: (val: string) => void
  isUpsertModalOpen: boolean
  setIsUpsertModalOpen: (val: boolean) => void
  selectedRole: Role | null
  setSelectedRole: (val: Role | null) => void
  columns: { key: string; label: string }[]
  isColumnVisible: (key: string) => boolean
}

const Roles = ({
  searchTerm,
  setSearchTerm,
  isUpsertModalOpen,
  setIsUpsertModalOpen,
  selectedRole,
  setSelectedRole,
  columns,
  isColumnVisible,
}: RolesProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
  const debouncedSearch = useDebounce(searchTerm, 500)

  const visibleColumnsCount =
    columns.filter(c => isColumnVisible(c.key)).length + 1

  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation()

  const { data, isLoading, isFetching, isError } = useGetRolesQuery({
    search: debouncedSearch,
  })

  useEffect(() => {
    if (isFetching && !isLoading) {
      toast.loading('Loading roles...', {
        id: 'roles-fetching-toast',
      })
    } else {
      toast.dismiss('roles-fetching-toast')
    }
    return () => {
      toast.dismiss('roles-fetching-toast')
    }
  }, [isFetching, isLoading])

  const roles = !isError && data?.success ? data?.data : []

  const handleEditClick = (role: Role) => {
    setSelectedRole(role)
    setIsUpsertModalOpen(true)
  }

  const handleModalClose = () => {
    setIsUpsertModalOpen(false)
    setSelectedRole(null)
  }

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return
    try {
      await deleteRole(roleToDelete.id).unwrap()
      toast.success('Role deleted successfully')
      setIsDeleteDialogOpen(false)
      setRoleToDelete(null)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to delete role')
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className='h-full flex flex-col space-y-4 overflow-hidden'
    >
      <div className='flex-1 min-h-0 overflow-auto'>
        <div className='bg-card rounded-sm border border-border shadow-sm relative'>
          <Table
            wrapperClassName='overflow-visible h-auto'
            className='[&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 border-none'
          >
            <TableHeader className='font-bold sticky top-0 z-20 shadow-sm'>
              <TableRow>
                {columns.map(column => {
                  if (!isColumnVisible(column.key)) return null
                  return (
                    <TableHead
                      key={column.key}
                      className={`font-bold bg-muted/50 ${
                        column.key === 'role_name'
                          ? 'min-w-[150px]'
                          : column.key === 'created_date'
                            ? 'text-muted-foreground min-w-[120px]'
                            : 'text-muted-foreground text-center truncate'
                      }`}
                    >
                      {column.label}
                    </TableHead>
                  )
                })}
                <TableHead className='text-right font-bold text-muted-foreground min-w-[120px] sticky right-0 z-30 bg-muted/50'>
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence mode='wait'>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`role-skeleton-${i}`}>
                      <TableCell colSpan={visibleColumnsCount}>
                        <Skeleton className='h-8 w-full' />
                      </TableCell>
                    </TableRow>
                  ))
                ) : roles.length > 0 ? (
                  roles.map((role: Role, index: number) => (
                    <motion.tr
                      key={role.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className='group border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-default'
                    >
                      {columns.map(column => {
                        if (!isColumnVisible(column.key)) return null
                        if (column.key === 'role_name') {
                          return (
                            <TableCell key={column.key}>
                              <div className='flex items-center gap-3'>
                                <div className='h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs border border-primary/20 transition-transform group-hover:scale-110'>
                                  <Shield className='h-4 w-4' />
                                </div>
                                <span className='font-semibold text-foreground group-hover:text-primary transition-colors capitalize'>
                                  {role.name}
                                </span>
                              </div>
                            </TableCell>
                          )
                        }
                        if (column.key === 'created_date') {
                          return (
                            <TableCell key={column.key}>
                              <div className='flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors'>
                                <Calendar className='h-3.5 w-3.5' />
                                <span className='text-sm whitespace-nowrap'>
                                  {role?.created_at
                                    ? format(
                                        new Date(role?.created_at),
                                        'MMM dd, yyyy'
                                      )
                                    : '-'}
                                </span>
                              </div>
                            </TableCell>
                          )
                        }

                        // It's a permission column
                        return (
                          <TableCell key={column.key} className='text-center'>
                            <div className='flex justify-center'>
                              {role[column.key as keyof Role] === 1 ? (
                                <div className='h-5 w-5 bg-primary rounded-md text-primary-foreground flex items-center justify-center shadow-sm'>
                                  <Check className='h-3.5 w-3.5 stroke-[4]' />
                                </div>
                              ) : (
                                <div className='h-5 w-5 bg-muted rounded-md text-muted-foreground/40 flex items-center justify-center border border-border'>
                                  <X className='h-3.5 w-3.5 stroke-[3]' />
                                </div>
                              )}
                            </div>
                          </TableCell>
                        )
                      })}
                      <TableCell className='sticky right-0 z-10 bg-background/80 backdrop-blur-sm border-l border-border shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]'>
                        <div className='flex items-center justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-8 w-8 hover:bg-muted hover:text-foreground transition-all'
                            disabled={role.id == 1}
                            onClick={() => handleEditClick(role)}
                          >
                            <Edit className='h-5 w-5' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-8 w-8 hover:bg-destructive/10 hover:text-destructive transition-all '
                            disabled={role.id == 1}
                            onClick={() => handleDeleteClick(role)}
                          >
                            <Trash className='h-5 w-5' />
                          </Button>
                        </div>
                      </TableCell>
                    </motion.tr>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnsCount}
                      className='h-64 text-center'
                    >
                      <div className='flex flex-col items-center justify-center text-muted-foreground gap-2'>
                        <Shield className='h-10 w-10 text-muted-foreground/20' />
                        <p className='text-lg font-medium text-muted-foreground/60'>
                          No roles found
                        </p>
                        {searchTerm && (
                          <Button
                            variant='link'
                            onClick={() => {
                              setSearchTerm('')
                            }}
                            className='text-primary hover:text-primary/80 underline-offset-4'
                          >
                            Clear search
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>

      <UpsertRole
        isOpen={isUpsertModalOpen}
        onClose={handleModalClose}
        role={selectedRole}
      />

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={open => !open && setIsDeleteDialogOpen(false)}
      >
        <DialogContent className='max-w-[400px] p-0 overflow-hidden border-none shadow-2xl rounded-2xl bg-card'>
          <div className='p-6 pt-8 flex flex-col items-center text-center'>
            <div className='w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4 border border-destructive/20 shadow-sm'>
              <AlertTriangle className='w-8 h-8 text-destructive animate-pulse' />
            </div>
            <DialogHeader className='p-0 mb-2'>
              <DialogTitle className='text-xl font-bold text-foreground'>
                Delete Role?
              </DialogTitle>
            </DialogHeader>
            <p className='text-muted-foreground text-sm leading-relaxed'>
              Are you sure you want to delete the{' '}
              <span className='font-bold text-foreground'>
                "{roleToDelete?.name}"
              </span>{' '}
              role? This action cannot be undone and may affect users assigned
              to this role.
            </p>
          </div>

          <div className='p-4 bg-muted flex gap-3 border-t border-border'>
            <Button
              variant='outline'
              onClick={() => setIsDeleteDialogOpen(false)}
              className='flex-1 h-11 font-bold rounded-xl border-border bg-background hover:bg-muted transition-all text-sm text-muted-foreground'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className='flex-1 h-11 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-bold rounded-xl shadow-lg shadow-destructive/20 transition-all text-sm'
            >
              {isDeleting ? 'Deleting...' : 'Delete Role'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default Roles
